import {Injectable} from '@angular/core';
import {BimSection, BimSectionPlan} from '../../../bp-common/models/plan';
import {ViewerReadyParams} from '../../../bim-viewer/src/lib/models/ready-params';
import {LatLng, Point} from 'leaflet';
import {PlanService, TileViewerReadyEvent} from '@mydocma/tile-viewer';
import {PlanLocationService} from './plan-location.service';
import {UntilDestroy} from '@ngneat/until-destroy';
import {Subscription} from 'rxjs';
import {
  CoordinatesTransformationService
} from '../coordinates-transformation-service/coordinates-transformation.service';
import {CameraPosition} from '../../../bim-viewer/src/lib/models/pin';
import {CameraChangeInfo} from '../../../bim-viewer/src/lib/services/camera.service';

export type planId = string;

@UntilDestroy()
@Injectable()
export class LocationService {
  public planReady: TileViewerReadyEvent;
  // Camera Functions
  public planLocationCollection = new Map<planId, CameraPosition>();
  private bimSection: BimSection;
  private bimPlan: BimSectionPlan;
  private bimViewerReadParamData: ViewerReadyParams;
  private markerLocationChangeSubscription: Subscription;
  private cameraRotationChangeSubscription: Subscription;
  private planService: PlanService;


  constructor(
    private planLocationService: PlanLocationService,
    private coordinatesTransformationService: CoordinatesTransformationService,
  ) {

  }

  attach(planReady, bimViewerReadParamData, {bimPlan, section}) {
    this.bimViewerReadParamData = bimViewerReadParamData;
    this.planReady = planReady;
    this.planService = this.planReady.mapService as PlanService;
    this.bimPlan = bimPlan;
    this.bimSection = section;
  }


  public setLocationPoint(): void {
    const initialCamera = this.getInitialCamera();
    const initialLocationPoint = this.coordinatesTransformationService.transformToPlanCoordinates(initialCamera.position);

    this.updatePlanLocationCollection(initialCamera);
    this.updatePinAndCameraLocations(initialLocationPoint, initialCamera);

    this.setupMarkerLocationChangeSubscription();
  }

  private getInitialCamera(): CameraPosition {
    const cachedCamera = this.planLocationCollection.get(this.bimPlan.planId);
    if (cachedCamera) {
      return cachedCamera;
    }

    const centerLatLng = this.planService.visiblePlanBounds.getCenter();
    const centerPoint = this.getCenterPoint(centerLatLng, this.planService.plan.maxZoom);
    const centerBimPoint = this.coordinatesTransformationService.transformToModelCoordinates(centerPoint);
    return {
      target: {
        x: centerBimPoint.x,
        y: centerBimPoint.y + 1,
        z: this.bimSection.z
      },
      position: {
        ...centerBimPoint,
        z: this.bimSection.z
      }
    };
  }


  private updatePlanLocationCollection(camera: CameraPosition): void {
    this.planLocationCollection.set(this.bimPlan.planId, camera);
  }

  private updatePinAndCameraLocations(planPoint, camera: CameraPosition): void {
    const cameraService = this.bimViewerReadParamData.cameraService;
    this.planLocationService.setLocationMarker(planPoint);
    this.bimViewerReadParamData.cameraService.setCameraPosition(camera);
    cameraService.enableViewerBehaveLikePanorama();
  }


  private getCenterPoint(centerLatLng: LatLng, maxZoom: number): Point {
    return this.planService.map.project(centerLatLng, maxZoom);
  }

  private setupMarkerLocationChangeSubscription(): void {
    this.markerLocationChangeSubscription?.unsubscribe();
    this.cameraRotationChangeSubscription?.unsubscribe();
    this.markerLocationChangeSubscription = this.planLocationService.onMarkerLocationChange
      .subscribe((point: Point) => {
        this.setNewCameraPosition(point);
      });

    this.cameraRotationChangeSubscription = this.bimViewerReadParamData.cameraService.onCameraRotateChange
      .subscribe((value: CameraChangeInfo) => {
        this.handleCameraRotationChange(value);
      });
  }

  private handleCameraRotationChange(value: CameraChangeInfo): void {
    this.planLocationCollection.set(this.bimPlan.planId, value.camera);
    this.planLocationService.setLocationMarkerAngle(value.angleDegrees);
  }


  private setNewCameraPosition(point: Point) {
    const position = this.coordinatesTransformationService.transformToModelCoordinates(point);
    let cameraPosition = this.planLocationCollection.get(this.bimPlan.planId);
    if (cameraPosition) {
      const oldPosition = new THREE.Vector3(cameraPosition.position.x, cameraPosition.position.y, cameraPosition.position.z);
      cameraPosition.position = {...position, z: this.bimSection.z};
      const oldTarget = new THREE.Vector3(cameraPosition.target.x, cameraPosition.target.y, cameraPosition.target.z);
      const newPosition = new THREE.Vector3(cameraPosition.position.x, cameraPosition.position.y, cameraPosition.position.z);
      const translationVector = newPosition.sub(oldPosition); // {x: 1, y: 1, z: 1}
      const newTarget = oldTarget.add(translationVector); //
      cameraPosition.target = newTarget;
    } else {
      // cameraPosition = {...position};
    }
    this.bimViewerReadParamData.cameraService.setCameraPosition(cameraPosition);
    this.planLocationCollection.set(this.bimPlan.planId, cameraPosition);
  }
}
